import React, { useEffect, useState } from 'react'
import cx from 'classnames'
import { get } from 'lodash-es'
import Segment from '../../../atoms/Segment/Segment'
import NewBeneficiary from '../../../molecules/NewBeneficiary/NewBeneficiary'
import ArrowNext from '../../../atoms/Svgs/ArrowNext'
import DraggableList from '../../../molecules/DraggableList/DraggableList'
import Beneficiary from './Beneficiary/Beneficiary'
import DepositBeneficiary from './DepositBeneficiary/DepositBeneficiary'
import styles from './FundDistribution.module.scss'
import {
  partyBeneficiary,
  depositBeneficiary,
  easyPayBeneficiary,
  consolidatedWalletBeneficiary,
} from '../../../../../modules/finance'
import { forEach, isNil, map, pathOr } from 'ramda'
import FormErrors from '../../../molecules/FormErrors/FormErrors'
import EditReference from './EditReference/EditReference'
import { depositBeneficiaryReferences } from 'modules/depositManagement/constants'
import { depositInvoiceTypes } from 'modules/api/invoice/constants'
import { useSelector } from 'react-redux'
import { partyApiSelectors } from 'modules/api/party'
import { reconApiSelectors } from 'modules/api/recon'
import { isBeneficiarySettled } from 'modules/api/recon/utils'
import { isDepositManagedByOwnerWithoutBankDetails, shouldZeroBeneficiaryAmount } from './fundDistributionUtils'

interface IFundDistributionProps {
  state?: 'default' | 'info' | 'warning' | 'danger'
  beforeContent?: React.ReactNode | boolean
  afterHeader?: React.ReactNode | boolean
  headerText?: string
  invoiceId?: string
  invoiceType: {
    name: string | null
    value: string | null
  }
  invoiceStatus?: string | null
  balance?: number
  unallocatedAmount?: number
  amountPlaceholderWhenBalanceNotSet?: string
  includeVatCalculatorOnPaymentRules?: boolean
  onNewBeneficiaryAdded?: ((beneficiary: any) => void) | ((beneficiary: any, index: number) => void)
  onBeneficiaryRemoved?: (index: number) => void
  onNewBeneficiaryAmountChange?: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void
  onBeneficiaryReferenceChange?: (index: number, updatedReference: string | null, type: string) => void
  onBeneficiariesOrderChange?: (newOrder: number[]) => void
  onDepositBeneficiaryTransferChange?: (index: number, transfer: boolean) => void
  beneficiariesKey?: string | null
  newBeneficiaryKeyPrefix?: string
  beneficiaries: any[]
  partySearchTags?: string
  partyTags?: {
    value: string
    label: string
  }[]
  getDefaultResultSets?: (searchTerm: string) => any
  errors?: any
  beneficiaryAmountPlaceholder?: string
  onVatChange?: ((index: number, e: React.ChangeEvent<HTMLInputElement>) => void) | ((...args: any[]) => void)
  readonly?: boolean
  isNewBeneficiaryAllowed?: boolean
  fetchEasyPayData?: (reference: string) => void
  easyPayData?: any
  propertyOwnerId?: string | null
  propertyOwnerName?: string | null
  propertyOwnerBankDetails?: object
  invoiceVatEnabled?: boolean
  disableDepositEdit?: boolean
  /**
   * Set on invoice templates (lease invoices), where a deposit's payment rule mirrors the
   * invoice amount instead of being captured per beneficiary. The deposit amount is then
   * read-only, and is always displayed — also when the deposit is self-managed by an owner
   * without bank details, where an amount that pays out to a bank account would be zeroed.
   */
  isDepositAmountDerivedFromInvoice?: boolean
  Footer?: React.ComponentType
  inputTooltipText?: string
  portfolioId?: string
}

const FundDistribution = ({
  state,
  beforeContent,
  afterHeader,
  headerText = 'Payment Allocation',
  invoiceId,
  invoiceType = { name: null, value: null },
  invoiceStatus,
  balance,
  unallocatedAmount,
  amountPlaceholderWhenBalanceNotSet,
  includeVatCalculatorOnPaymentRules = true,
  onNewBeneficiaryAdded,
  onBeneficiaryRemoved,
  onNewBeneficiaryAmountChange,
  onBeneficiaryReferenceChange,
  onBeneficiariesOrderChange,
  onDepositBeneficiaryTransferChange,
  onVatChange,
  beneficiariesKey,
  newBeneficiaryKeyPrefix = '',
  beneficiaries = [],
  partySearchTags = '',
  getDefaultResultSets,
  partyTags,
  errors,
  beneficiaryAmountPlaceholder,
  readonly = false,
  isNewBeneficiaryAllowed = true,
  fetchEasyPayData,
  easyPayData,
  propertyOwnerId,
  propertyOwnerName,
  propertyOwnerBankDetails,
  invoiceVatEnabled = false,
  disableDepositEdit = false,
  isDepositAmountDerivedFromInvoice = false,
  Footer,
  inputTooltipText,
  portfolioId,
}: IFundDistributionProps): React.ReactElement => {
  const [newBeneficiaryIndex, setNewBeneficiaryIndex] = useState(-1)
  const [showAddButton, setShowAddButton] = useState(true)

  const getPartyBankDetails = useSelector(partyApiSelectors.getPartyBankDetails)
  const getPartyById = useSelector(partyApiSelectors.getPartyById)
  const getReconStatus = useSelector(reconApiSelectors.getReconStatusByInvoiceId)

  const isDeposit = depositInvoiceTypes.includes(invoiceType.value ?? '')
  const isDone = invoiceStatus === 'Done'
  const readOnlyMode = isDeposit || readonly
  const depositManagedBy: 'owner' | 'agency' =
    beneficiaries?.[0]?.beneficiary?.value?.beneficiaryTag === 'Owner' ? 'owner' : 'agency'
  const errorItems: any = []

  useEffect(() => {
    if (isDeposit || isDone) {
      setShowAddButton(false)
    } else {
      setShowAddButton(true)
    }
  }, [isDeposit, isDone])

  const handleAdd = (): void => {
    setNewBeneficiaryIndex(beneficiaries.length)
    setShowAddButton(false)
  }

  const handleRemove = (index: number): void => {
    onBeneficiaryRemoved?.(index)
  }

  const resetNewBeneficiaryIndex = (): void => {
    setNewBeneficiaryIndex(-1)
    setShowAddButton(true)
  }

  const handleNewBeneficiaryAdded = (beneficiary: any): void => {
    onNewBeneficiaryAdded?.({ beneficiary }, beneficiaries.length)
    resetNewBeneficiaryIndex()
  }

  const getAmountPlaceholder = (index: number): string => {
    let placeholder

    if (isBeneficiarySettled(get(beneficiaries, [index]))) {
      placeholder = 'Settled'
    } else if (beneficiaryAmountPlaceholder) {
      placeholder = beneficiaryAmountPlaceholder
    } else if (!!balance && amountPlaceholderWhenBalanceNotSet) {
      placeholder = amountPlaceholderWhenBalanceNotSet
    } else if (get(beneficiaries, [index]).beneficiary.value.amount === 0) {
      placeholder = 'Set payment rule'
    } else {
      /** Remove balance placeholder for now until auto distribution logic is re-introduced */
      // placeholder = (index + 1 === beneficiaries.length) ? 'Balance' : 'Set amount'
      placeholder = '0.00'
    }

    return placeholder
  }

  const classes = cx(styles.root, {
    [styles['status-ready']]: invoiceStatus === 'Ready',
  })

  // Track original indices for all beneficiaries
  const originalIndicesRef = React.useRef<number[]>([])

  const result = (() => {
    const items: any[] = []
    const originalIndicesList: number[] = []
    const completeIndicesMap: Set<number> = new Set()
    ;(beneficiaries || []).forEach((beneficiary: any, i: number) => {
      if (!beneficiary?.beneficiary) {
        // Include placeholder for incomplete beneficiaries but track original index
        items.push({
          itemProps: { isDraggable: false, disableRemove: true, style: {} },
          children: <></>,
        })
        originalIndicesList.push(i)
        return
      }

      const currentItemIndex = items.length
      completeIndicesMap.add(currentItemIndex)
      originalIndicesList.push(i)

      const prefix = `beneficiaries[${i}]`
      const {
        Meta = false,
        isRemovable = true,
        isDraggable = true,
        beneficiary: { type, value: beneficiaryValue },
      } = beneficiary

      const { beneficiaryName, referenceContext, reference, vatApplied } = (() =>
        type === 'EasyPayBeneficiary'
          ? {
              beneficiaryName: get(
                easyPayData,
                [beneficiaryValue.easyPayReference, 'request', 'receiver'],
                `EasyPay - ${beneficiaryValue.beneficiaryTag as string}`,
              ),
              referenceContext: 'EasyPay Payment',
              reference: beneficiaryValue.easyPayReference,
              vatApplied: !isNil(beneficiaryValue.vat) ? beneficiaryValue.vat : invoiceVatEnabled,
            }
          : type === 'ConsolidatedWalletBeneficiary'
          ? {
              beneficiaryName: beneficiaryValue.name,
              referenceContext: 'Owner Wallet Transfer',
              reference: beneficiaryValue.reference,
              vatApplied: !isNil(beneficiaryValue.vat) ? beneficiaryValue.vat : invoiceVatEnabled,
            }
          : beneficiaryValue.partyTag === 'Agency' && isDone
          ? {
              beneficiaryName: beneficiaryValue.name,
              referenceContext: 'Commission Wallet Transfer',
              reference: '',
              vatApplied: !isNil(beneficiaryValue.vat) ? beneficiaryValue.vat : invoiceVatEnabled,
            }
          : beneficiaryValue.partyTag === 'Agency'
          ? {
              beneficiaryName: beneficiaryValue.name,
              referenceContext: 'Commission Wallet Transfer',
              reference: beneficiaryValue.transfer ? depositBeneficiaryReferences.reos : beneficiaryValue.reference,
              vatApplied: !isNil(beneficiaryValue.vat) ? beneficiaryValue.vat : invoiceVatEnabled,
            }
          : isDone
          ? {
              beneficiaryName: beneficiaryValue.name,
              referenceContext: false,
              reference: beneficiaryValue.transfer ? depositBeneficiaryReferences.reos : '',
              vatApplied: !isNil(beneficiaryValue.vat) ? beneficiaryValue.vat : invoiceVatEnabled,
            }
          : {
              beneficiaryName: beneficiaryValue.name,
              referenceContext: beneficiaryValue.partyId,
              reference: beneficiaryValue.transfer ? depositBeneficiaryReferences.reos : beneficiaryValue.reference,
              vatApplied: !isNil(beneficiaryValue.vat) ? beneficiaryValue.vat : invoiceVatEnabled,
            })()

      const { amount, amountToPay } = beneficiaryValue

      const value = typeof amountToPay === 'number' ? amountToPay : amount > 0 ? amount.toString() : ''

      const bankDetails = getPartyBankDetails(beneficiaryValue.partyId)
      const isPartyLoaded = Boolean(getPartyById(beneficiaryValue.partyId))
      const reconStatus = getReconStatus(invoiceId)

      // A self-managed-by-owner deposit pays out to the owner's account; with no
      // owner bank details there is nowhere to pay, so its amount is zeroed too.
      const depositManagedByOwnerNoBank =
        type === depositBeneficiary.type &&
        isDepositManagedByOwnerWithoutBankDetails({
          transfer: Boolean(beneficiaryValue.transfer),
          managedByValue: depositManagedBy,
          propertyOwnerId,
          propertyOwnerBankDetails,
        })

      const settled = isBeneficiarySettled(beneficiary)
      const shouldZeroAmount =
        shouldZeroBeneficiaryAmount({
          isSettled: settled,
          isManagedByReos: Boolean(beneficiaryValue.transfer),
          isWalletTransfer: type === consolidatedWalletBeneficiary.type,
          reconStatus: String(reconStatus),
          isPartyLoaded,
          bankDetails,
        }) || depositManagedByOwnerNoBank

      const sharedProps = {
        id: i,
        index: i,
        includeVatCalculator: includeVatCalculatorOnPaymentRules,
        onNewBeneficiaryAmountChange: onNewBeneficiaryAmountChange
          ? onNewBeneficiaryAmountChange.bind(null, i)
          : undefined,
        placeholder: getAmountPlaceholder(i),
        amountName: `${prefix}.amount`,
        value: type === depositBeneficiary.type ? value : shouldZeroAmount ? '' : value,
        disableAmount: readOnlyMode || shouldZeroAmount || beneficiary.originalAmount === 0,
        onVatChange: typeof onVatChange === 'function' ? onVatChange.bind(null, i) : undefined,
        referenceContext,
        reference:
          typeof onBeneficiaryReferenceChange === 'function' &&
          !beneficiaryValue.transfer &&
          type !== 'EasyPayBeneficiary' ? (
            <EditReference
              reference={reference}
              onReferenceUpdate={(updatedReference: any) => onBeneficiaryReferenceChange(i, updatedReference, type)}
            />
          ) : (
            reference
          ),
        beneficiaryName,
        vatApplied: disableDepositEdit && type === 'DepositBeneficiary' ? false : vatApplied,
        isBeneficiarySettled: isBeneficiarySettled(beneficiary),
        amountPayable: beneficiary.originalAmount,
        Meta,
        inputTooltipText,
      }

      const preValidationErrors = pathOr([], ['beneficiary', 'errors'], beneficiary)

      if (preValidationErrors.length > 0) {
        errorItems.push(i)
      }

      const errorClasses = cx(styles['item-wrapper'], {
        [styles['error-item-wrapper']]: preValidationErrors.length > 0,
      })

      const calcItemMarginTop = (): string => {
        let margin = 10 + i * 2
        margin += preValidationErrors.length > 0 ? 4 : 0

        let totalErrorsAbove = 0
        map((errorItemIndex: number) => {
          totalErrorsAbove += errorItemIndex < i ? 75 : 0
        }, errorItems)

        margin += totalErrorsAbove

        return `${margin}px`
      }

      let item: any
      switch (type) {
        case partyBeneficiary.type:
        case easyPayBeneficiary.type:
        case consolidatedWalletBeneficiary.type:
          item = {
            itemProps: {
              disableRemove:
                (!isRemovable && 'Unable to remove a partially allocated beneficiary') ||
                (isDeposit && 'Unable to remove a deposit beneficiary'),
              isDraggable: Boolean(isDraggable),
              style: {
                marginTop: i === 0 ? 0 : calcItemMarginTop(),
              },
            },
            children: (
              <div className={errorClasses}>
                {preValidationErrors.length > 0 && (
                  <span className={styles.error}>
                    {preValidationErrors.map((error: any, i: any) => (
                      <span key={i}>{error.message}</span>
                    ))}
                  </span>
                )}
                <Beneficiary {...sharedProps} />
              </div>
            ),
          }
          break
        case depositBeneficiary.type:
          item = {
            itemProps: {
              disableRemove: 'Unable to remove a deposit beneficiary',
              isDraggable: Boolean(isDraggable),
              style: {},
            },
            children: (
              <DepositBeneficiary
                {...sharedProps}
                transfer={beneficiaryValue.transfer}
                disableTransferToggle={!isRemovable}
                onTransferChange={onDepositBeneficiaryTransferChange || (() => {})}
                propertyOwnerId={propertyOwnerId}
                propertyOwnerName={propertyOwnerName}
                propertyOwnerBankDetails={propertyOwnerBankDetails}
                disableCurrencyField={
                  disableDepositEdit || isDepositAmountDerivedFromInvoice || depositManagedByOwnerNoBank
                }
                shouldDisplayValueAsZero={shouldZeroAmount && !isDepositAmountDerivedFromInvoice}
                managedByValue={depositManagedBy}
                invoiceStatus={invoiceStatus}
              />
            ),
          }
          break
        default:
          throw new Error(`Unknown beneficiary type: ${type}`)
      }

      items.push(item)
    })

    // Update ref with current originalIndices after building all items
    originalIndicesRef.current = originalIndicesList

    return { beneficiaryItems: items }
  })()

  const beneficiaryItems = result.beneficiaryItems

  const handleBeneficiaryReorder = (newOrder: number[]): void => {
    if (typeof onBeneficiariesOrderChange !== 'function') return
    const mappedOrder = newOrder.map(i => originalIndicesRef.current[i])
    onBeneficiariesOrderChange(mappedOrder)
  }

  const calcListHeight = (): number => {
    let listHeight = 0

    forEach(() => {
      listHeight += 120
    }, beneficiaryItems)

    listHeight = listHeight + errorItems.length * 75

    return listHeight
  }

  return (
    <div className={classes}>
      <Segment.Group state={state}>
        {beforeContent && beforeContent}

        <Segment horizontal>
          <Segment.Label icon={<ArrowNext />} text={headerText} />
        </Segment>

        <Segment>
          {afterHeader && afterHeader}

          {errors?.general && <FormErrors errors={[errors.general]} />}
          {errors?.beneficiaries && <FormErrors errors={[errors.beneficiaries]} />}

          <DraggableList
            key={beneficiariesKey} // used to reset order state, undefined won't do anyhing AFAIK
            items={beneficiaryItems}
            onMove={handleBeneficiaryReorder}
            onAdd={!readOnlyMode && isNewBeneficiaryAllowed ? handleAdd : () => {}}
            onRemove={readOnlyMode ? () => {} : handleRemove}
            listHeight={calcListHeight()}
            addItemText="Add payment rule"
            addItemIcon={false}
            showAddButton={isNewBeneficiaryAllowed && showAddButton}
          />

          {newBeneficiaryIndex !== -1 && (
            <NewBeneficiary
              key={`${newBeneficiaryKeyPrefix}${newBeneficiaryIndex}`}
              onNewBeneficiaryAdded={handleNewBeneficiaryAdded}
              tags={partySearchTags || ''}
              onCancel={resetNewBeneficiaryIndex}
              getDefaultResultSets={getDefaultResultSets}
              // @link https://app.clickup.com/t/28u39z7
              partyTags={(partyTags || []).filter(
                tag => !['AgencyDepositAccount', 'TenantDepositAccount'].includes(tag.value),
              )}
              easyPayData={easyPayData}
              fetchEasyPayData={fetchEasyPayData || (() => {})}
              portfolioId={portfolioId || ''}
              primaryOwnerPartyId={propertyOwnerId || ''}
              propertyOwnerName={propertyOwnerName || ''}
              unallocatedAmount={unallocatedAmount || 0}
            />
          )}
        </Segment>

        {Footer && <Footer />}
      </Segment.Group>
    </div>
  )
}

export default FundDistribution
